import { useEffect } from "react";
import { toast } from "utils/toast";
import { canRefreshApp } from "./useAppRefreshBlocker";

const VERSION_CHECK_INTERVAL_MS = 60_000;
const VERSION_FETCH_TIMEOUT_MS = 15_000;
const VERSION_URL = "/version.json";

interface AppVersionResponse {
  version?: string;
}

export const useAppVersionRefresh = () => {
  useEffect(() => {
    if (!import.meta.env.PROD || import.meta.env.VITE_ENVIRONMENT === "local") {
      return undefined;
    }

    let isChecking = false;
    let hasUpdateAvailable = false;
    let isMounted = true;

    const isHidden = () => document.visibilityState === "hidden";

    const refresh = () => {
      if (!canRefreshApp()) {
        toast.info(
          "Save or discard your changes and finish uploads before refreshing.",
        );
        return;
      }
      window.location.reload();
    };

    const refreshHiddenTab = () => {
      if (!isHidden() || !canRefreshApp()) return false;
      window.location.reload();
      return true;
    };

    const checkVersion = async () => {
      if (hasUpdateAvailable) {
        refreshHiddenTab();
        return;
      }
      if (isChecking) return;

      isChecking = true;
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        VERSION_FETCH_TIMEOUT_MS,
      );

      try {
        const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as AppVersionResponse;
        const deployedVersion = data.version;

        if (
          isMounted &&
          deployedVersion &&
          deployedVersion !== import.meta.env.VITE_APP_VERSION
        ) {
          hasUpdateAvailable = true;
          // Hidden tabs still parse live documents; reload once local work is safe.
          if (refreshHiddenTab()) return;
          toast.info("New version available", {
            action: {
              label: "Refresh",
              onClick: refresh,
            },
            duration: Number.POSITIVE_INFINITY,
          });
        }
      } catch {
        return;
      } finally {
        window.clearTimeout(timeoutId);
        isChecking = false;
      }
    };

    const intervalId = window.setInterval(
      checkVersion,
      VERSION_CHECK_INTERVAL_MS,
    );

    window.addEventListener("focus", checkVersion);
    document.addEventListener("visibilitychange", checkVersion);

    checkVersion();

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkVersion);
      document.removeEventListener("visibilitychange", checkVersion);
    };
  }, []);
};
